<script>
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import BubbleActions from './bubble/Actions.vue';
import BubbleContact from './bubble/Contact.vue';
import BubbleFile from './bubble/File.vue';
import BubbleImageAudioVideo from './bubble/ImageAudioVideo.vue';
import BubbleIntegration from './bubble/Integration.vue';
import BubbleLocation from './bubble/Location.vue';
import BubbleMailHead from './bubble/MailHead.vue';
import BubbleReplyTo from './bubble/ReplyTo.vue';
import BubbleText from './bubble/Text.vue';
import ContextMenu from 'dashboard/modules/conversations/components/MessageContextMenu.vue';
import InstagramStory from './bubble/InstagramStory.vue';
import InstagramStoryReply from './bubble/InstagramStoryReply.vue';
import Spinner from 'shared/components/Spinner.vue';
import { CONTENT_TYPES } from 'shared/constants/contentType';
import { MESSAGE_TYPE, MESSAGE_STATUS } from 'shared/constants/messages';
import { generateBotMessageContent } from './helpers/botMessageContentHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { getDayDifferenceFromNow } from 'shared/helpers/DateHelper';
import * as Sentry from '@sentry/vue';
import { useTrack } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { mapGetters } from 'vuex';

export default {
  components: {
    BubbleActions,
    BubbleContact,
    BubbleFile,
    BubbleImageAudioVideo,
    BubbleIntegration,
    BubbleLocation,
    BubbleMailHead,
    BubbleReplyTo,
    BubbleText,
    ContextMenu,
    InstagramStory,
    InstagramStoryReply,
    Spinner,
  },
  props: {
    data: {
      // 消息数据对象
      type: Object,
      required: true,
    },
    isATweet: {
      // 是否是推文
      type: Boolean,
      default: false,
    },
    isAFacebookInbox: {
      // 是否是Facebook
      type: Boolean,
      default: false,
    },
    isInstagram: {
      // 是否是Instagram
      type: Boolean,
      default: false,
    },
    isAWhatsAppChannel: {
      // 是否是WhatsApp
      type: Boolean,
      default: false,
    },
    isAnEmailInbox: {
      // 是否是Email
      type: Boolean,
      default: false,
    },
    inboxSupportsReplyTo: {
      // 支持回复
      type: Object,
      default: () => ({}),
    },
    inReplyTo: {
      // 回复消息对象
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const { formatMessage } = useMessageFormatter(); // 格式化消息
    return {
      formatMessage,
    };
  },
  data() {
    return {
      showContextMenu: false, // 是否显示更多选项菜单
      hasMediaLoadError: false, // 是否有媒体加载错误
      contextMenuPosition: {}, // 更多选项菜单位置
      showBackgroundHighlight: false, // 是否显示背景高亮
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
      translatedMessageIds: 'getTranslatedMessageIds',
    }),

    // 是否显示翻译内容，从 Vuex store 中获取
    showTranslation() {
      return this.translatedMessageIds.includes(this.data.id);
    },
    // 获取当前语言的翻译内容
    translatedContent() {
      const { locale } = this.getAccount(this.currentAccountId);
      const translations = this.contentAttributes.translations || {};
      return translations[locale] || '';
    },

    // 格式化翻译内容，使用与原始消息相同的格式化方式
    formattedTranslatedContent() {
      // // 如果是电子邮件内容，直接返回翻译内容
      // if (this.isEmailContentType) {
      //   return this.translatedContent;
      // }

      // 处理翻译内容中丢失的换行符
      let processedContent = this.translatedContent;
      // 在翻译内容后添加“（翻译）”提示
      const translatedText = `${processedContent} （${this.$t('CONVERSATION.CONTEXT_MENU.TRANSLATE')}）`;

      // 对处理后的翻译内容使用相同的格式化函数
      return this.formatMessage(
        translatedText,
        this.isATweet,
        this.data.private
      );
    },

    // 判断消息是否已翻译
    hasTranslation() {
      return !!this.translatedContent && this.showTranslation;
    },

    attachments() {
      // Here it is used to get sender and created_at for each attachment
      // 用于获取每个消息附件的sender和created_at
      return this.data?.attachments.map(attachment => ({
        ...attachment,
        sender: this.data.sender || {},
        created_at: this.data.created_at || '',
      }));
    },
    hasOneDayPassed() {
      // Disable retry button if the message is failed and the message is older than 24 hours
      return getDayDifferenceFromNow(new Date(), this.data?.created_at) >= 1;
    },
    shouldRenderMessage() {
      return (
        this.hasAttachments ||
        this.data.content ||
        this.isEmailContentType ||
        this.isUnsupported ||
        this.isAnIntegrationMessage
      );
    },
    // 电子邮件消息内容
    emailMessageContent() {
      const {
        html_content: { full: fullHTMLContent } = {},
        text_content: { full: fullTextContent } = {},
      } = this.contentAttributes.email || {};

      if (fullHTMLContent) {
        return fullHTMLContent;
      }

      if (fullTextContent) {
        return fullTextContent.replace(/\n/g, '<br>');
      }

      return '';
    },
    displayQuotedButton() {
      if (this.emailMessageContent.includes('<blockquote')) {
        return true;
      }

      if (!this.isIncoming) {
        return false;
      }

      return false;
    },
    // 格式化后的消息内容，处理不同类型的消息（文本、HTML、机器人消息等）
    message() {
      // If the message is an email, emailMessageContent would be present
      // In that case, we would use letter package to render the email
      if (this.emailMessageContent && this.isIncoming) {
        return this.emailMessageContent;
      }

      // 结束会话后的评价消息
      // Please rate the conversation
      // Rating
      // 😍
      // Feedback
      // Very good!
      const botMessageContent = generateBotMessageContent(
        this.contentType,
        this.contentAttributes,
        {
          noResponseText: this.$t('CONVERSATION.NO_RESPONSE'),
          csat: {
            ratingTitle: this.$t('CONVERSATION.RATING_TITLE'),
            feedbackTitle: this.$t('CONVERSATION.FEEDBACK_TITLE'),
          },
        }
      );

      if (this.contentType === 'input_csat') {
        return this.$t('CONVERSATION.CSAT_REPLY_MESSAGE') + botMessageContent;
      }

      return (
        this.formatMessage(
          this.data.content,
          this.isATweet,
          this.data.private
        ) + botMessageContent
      );
    },
    inReplyToMessageId() {
      // Why not use the inReplyTo object directly?
      // Glad you asked! The inReplyTo object may or may not be available
      // depending on the current scroll position of the message list
      // since old messages are only loaded when the user scrolls up
      return this.data.content_attributes?.in_reply_to;
    },
    isAnInstagramStory() {
      return this.contentAttributes.image_type === 'story_mention';
    },
    // 消息更多选项菜单启用选项
    contextMenuEnabledOptions() {
      return {
        copy: this.hasText, // 复制
        delete: this.hasText || this.hasAttachments, // 撤回
        cannedResponse: this.isOutgoing && this.hasText, // 添加到预设回复（客服发的消息才能添加）
        replyTo: !this.data.private && this.inboxSupportsReplyTo.outgoing, // 回复消息
      };
    },
    // 消息的附加属性，包含翻译内容等
    contentAttributes() {
      return this.data.content_attributes || {};
    },
    externalError() {
      return this.contentAttributes.external_error || '';
    },
    sender() {
      return this.data.sender || {};
    },
    status() {
      return this.data.status;
    },
    storySender() {
      return this.contentAttributes.story_sender || null;
    },
    storyId() {
      return this.contentAttributes.story_id || null;
    },
    storyUrl() {
      return this.contentAttributes.story_url || null;
    },
    contentType() {
      const {
        data: { content_type: contentType },
      } = this;
      return contentType;
    },
    // Twitter用户个人资料链接
    twitterProfileLink() {
      const additionalAttributes = this.sender.additional_attributes || {};
      const { screen_name: screenName } = additionalAttributes;
      return `https://twitter.com/${screenName}`;
    },
    // 消息气泡对齐方式
    alignBubble() {
      const { message_type: messageType } = this.data;
      const isCentered = messageType === MESSAGE_TYPE.ACTIVITY;
      const isLeftAligned = messageType === MESSAGE_TYPE.INCOMING;
      const isRightAligned =
        messageType === MESSAGE_TYPE.OUTGOING ||
        messageType === MESSAGE_TYPE.TEMPLATE;
      return {
        center: isCentered,
        left: isLeftAligned,
        right: isRightAligned,
        'has-context-menu': this.showContextMenu,
        // this handles the offset required to align the context menu button
        // extra alignment is required since a tweet message has a the user name and avatar below it
        'has-tweet-menu': this.isATweet,
        'has-bg': this.showBackgroundHighlight,
      };
    },
    createdAt() {
      return this.contentAttributes.external_created_at || this.data.created_at;
    },
    isBubble() {
      return [0, 1, 3].includes(this.data.message_type);
    },
    // 是否是入站消息
    isIncoming() {
      return this.data.message_type === MESSAGE_TYPE.INCOMING;
    },
    // 是否是出站消息
    isOutgoing() {
      return this.data.message_type === MESSAGE_TYPE.OUTGOING;
    },
    // 是否是模板消息
    isTemplate() {
      return this.data.message_type === MESSAGE_TYPE.TEMPLATE;
    },
    // 是否是集成消息
    isAnIntegrationMessage() {
      return this.contentType === 'integrations';
    },
    // 电子邮件消息属性
    emailHeadAttributes() {
      return {
        email: this.contentAttributes.email,
        cc: this.contentAttributes.cc_emails,
        bcc: this.contentAttributes.bcc_emails,
      };
    },
    // 是否有附件
    hasAttachments() {
      return !!(this.data.attachments && this.data.attachments.length > 0);
    },
    // 消息是否被删除
    isMessageDeleted() {
      return this.contentAttributes.deleted;
    },
    // 是否有文本内容
    hasText() {
      return !!this.data.content;
    },
    // 出站发送者名称提示文本
    tooltipForSender() {
      const name = this.senderNameForAvatar;
      const { message_type: messageType } = this.data;
      const showTooltip =
        messageType === MESSAGE_TYPE.OUTGOING ||
        messageType === MESSAGE_TYPE.TEMPLATE;
      return showTooltip
        ? {
            content: `${this.$t('CONVERSATION.SENT_BY')} ${name}`,
          }
        : false;
    },
    // 发送失败提示
    errorMessageTooltip() {
      if (this.isFailed) {
        return this.externalError || this.$t(`CONVERSATION.SEND_FAILED`);
      }
      return '';
    },
    // 消息包装类（容器）
    wrapClass() {
      return {
        wrap: this.isBubble,
        'activity-wrap': !this.isBubble,
        'is-pending': this.isPending,
        'is-failed': this.isFailed,
        'is-email': this.isEmailContentType,
      };
    },
    // 消息气泡 CSS 类
    bubbleClass() {
      return {
        bubble: this.isBubble,
        'is-private': this.data.private,
        'is-unsupported': this.isUnsupported,
        'is-image': this.hasMediaAttachment('image'),
        'is-video': this.hasMediaAttachment('video'),
        'is-text': this.hasText,
        'is-from-bot': this.isSentByBot,
        'is-failed': this.isFailed,
        'is-email': this.isEmailContentType,
      };
    },
    // 是否不支持消息类型
    isUnsupported() {
      return this.contentAttributes.is_unsupported ?? false;
    },
    // 是否正在处理
    isPending() {
      return this.data.status === MESSAGE_STATUS.PROGRESS;
    },
    // 是否发送失败
    isFailed() {
      return this.data.status === MESSAGE_STATUS.FAILED;
    },
    // 是否是机器人发送的消息
    isSentByBot() {
      if (this.isPending || this.isFailed) return false;
      return !this.sender.type || this.sender.type === 'agent_bot';
    },
    // 是否显示消息右侧选项菜单
    shouldShowContextMenu() {
      return !(this.isFailed || this.isPending || this.isUnsupported);
    },
    showAvatar() {
      if (this.isOutgoing || this.isTemplate) {
        return true;
      }
      return this.isATweet && this.isIncoming && this.sender;
    },
    senderNameForAvatar() {
      if (this.isOutgoing || this.isTemplate) {
        const { name = this.$t('CONVERSATION.BOT') } = this.sender || {};
        return name;
      }
      return '';
    },
    isEmailContentType() {
      return this.contentType === CONTENT_TYPES.INCOMING_EMAIL;
    },
  },
  watch: {
    data() {
      this.hasMediaLoadError = false;
    },
  },
  mounted() {
    this.hasMediaLoadError = false;
    emitter.on(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL, this.closeContextMenu);
    this.setupHighlightTimer();
  },
  unmounted() {
    emitter.off(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL, this.closeContextMenu);
    clearTimeout(this.higlightTimeout);
  },
  methods: {
    isAttachmentImageVideoAudio(fileType) {
      return ['image', 'audio', 'video', 'story_mention', 'ig_reel'].includes(
        fileType
      );
    },
    hasMediaAttachment(type) {
      if (this.hasAttachments && this.data.attachments.length > 0) {
        return this.compareMessageFileType(this.data, type);
      }
      return false;
    },
    compareMessageFileType(messageData, type) {
      try {
        const { attachments = [{}] } = messageData;
        const { file_type: fileType } = attachments[0];
        return fileType === type && !this.hasMediaLoadError;
      } catch (err) {
        Sentry.setContext('attachment-parsing-error', {
          messageData,
          type,
          hasMediaLoadError: this.hasMediaLoadError,
        });

        Sentry.captureException(err);
        return false;
      }
    },
    handleContextMenuClick() {
      this.showContextMenu = !this.showContextMenu;
    },
    // 重试发送消息
    async retrySendMessage() {
      await this.$store.dispatch('sendMessageWithData', this.data);
    },
    onMediaLoadError() {
      this.hasMediaLoadError = true;
    },
    // 打开消息更多选项菜单
    openContextMenu(e) {
      const shouldSkipContextMenu =
        e.target?.classList.contains('skip-context-menu') ||
        e.target?.tagName.toLowerCase() === 'a';
      if (shouldSkipContextMenu || getSelection().toString()) {
        return;
      }

      e.preventDefault();
      if (e.type === 'contextmenu') {
        useTrack(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
      }
      this.contextMenuPosition = {
        x: e.pageX || e.clientX,
        y: e.pageY || e.clientY,
      };
      this.showContextMenu = true;
    },
    // 关闭消息更多选项菜单
    closeContextMenu() {
      this.showContextMenu = false;
      this.contextMenuPosition = { x: null, y: null };
    },
    // 处理回复消息
    handleReplyTo() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const { conversation_id: conversationId, id: replyTo } = this.data;

      LocalStorage.updateJsonStore(replyStorageKey, conversationId, replyTo);
      emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.data);
    },
    setupHighlightTimer() {
      if (Number(this.$route.query.messageId) !== Number(this.data.id)) {
        return;
      }

      this.showBackgroundHighlight = true;
      const HIGHLIGHT_TIMER = 1000;
      this.higlightTimeout = setTimeout(() => {
        this.showBackgroundHighlight = false;
      }, HIGHLIGHT_TIMER);
    },
    closeTranslation(id) {
      this.$store.commit('REMOVE_TRANSLATED_MESSAGE', id);
    },
  },
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <li
    v-if="shouldRenderMessage"
    :id="`message${data.id}`"
    class="group/context-menu"
    :class="[alignBubble]"
  >
    <div :class="wrapClass">
      <!-- 消息内容 -->
      <div
        v-if="isFailed && !hasOneDayPassed && !isAnEmailInbox"
        class="message-failed--alert"
      >
        <!-- 显示重试发送消息图标 -->
        <woot-button
          v-tooltip.top-end="$t('CONVERSATION.TRY_AGAIN')"
          size="tiny"
          color-scheme="alert"
          variant="clear"
          icon="arrow-clockwise"
          @click="retrySendMessage"
        />
      </div>
      <div :class="bubbleClass" @contextmenu="openContextMenu($event)">
        <!-- 不同类型消息内容 -->

        <!-- 邮件头消息 -->
        <BubbleMailHead
          :email-attributes="contentAttributes.email"
          :cc="emailHeadAttributes.cc"
          :bcc="emailHeadAttributes.bcc"
          :is-incoming="isIncoming"
        />
        <InstagramStoryReply v-if="storyUrl" :story-url="storyUrl" />
        <!-- 选择了消息更多菜单中的回复消息 -->
        <BubbleReplyTo
          v-if="inReplyToMessageId && inboxSupportsReplyTo.incoming"
          :message="inReplyTo"
          :message-type="data.message_type"
          :parent-has-attachments="hasAttachments"
        />
        <!-- 不支持的消息 -->
        <div v-if="isUnsupported">
          <template v-if="isAFacebookInbox && isInstagram">
            {{ $t('CONVERSATION.UNSUPPORTED_MESSAGE_INSTAGRAM') }}
          </template>
          <template v-else-if="isAFacebookInbox">
            {{ $t('CONVERSATION.UNSUPPORTED_MESSAGE_FACEBOOK') }}
          </template>
          <template v-else>
            {{ $t('CONVERSATION.UNSUPPORTED_MESSAGE') }}
          </template>
        </div>
        <!-- 文本消息 -->
        <BubbleText
          v-else-if="data.content"
          :message="message"
          :is-email="isEmailContentType"
          :display-quoted-button="displayQuotedButton"
        />
        <!-- 显示翻译内容 -->
        <div v-if="hasTranslation" class="translation-content">
          <div class="close-translation-row">
            <span
              class="close-translation-btn"
              @click="closeTranslation(data.id)"
            >
              {{ $t('CONVERSATION.CONTEXT_MENU.COLLAPSE') }}
              <span class="arrow-up">↑</span>
            </span>
          </div>
          <BubbleText
            :message="formattedTranslatedContent"
            :is-email="isEmailContentType"
            :display-quoted-button="displayQuotedButton"
          />
        </div>
        <!-- 集成消息 -->
        <BubbleIntegration
          :message-id="data.id"
          :content-attributes="contentAttributes"
          :inbox-id="data.inbox_id"
        />
        <!-- 正在上传附件提示 -->
        <span
          v-if="isPending && hasAttachments"
          class="chat-bubble has-attachment agent"
        >
          {{ $t('CONVERSATION.UPLOADING_ATTACHMENTS') }}
        </span>
        <!-- 附件 -->
        <div v-if="!isPending && hasAttachments">
          <div v-for="attachment in attachments" :key="attachment.id">
            <InstagramStory
              v-if="isAnInstagramStory"
              :story-url="attachment.data_url"
              @error="onMediaLoadError"
            />
            <!-- 图片/音频/视频 -->
            <BubbleImageAudioVideo
              v-else-if="isAttachmentImageVideoAudio(attachment.file_type)"
              :attachment="attachment"
              @error="onMediaLoadError"
            />
            <!-- 位置消息 -->
            <BubbleLocation
              v-else-if="attachment.file_type === 'location'"
              :latitude="attachment.coordinates_lat"
              :longitude="attachment.coordinates_long"
              :name="attachment.fallback_title"
            />
            <!-- 联系人信息 -->
            <BubbleContact
              v-else-if="attachment.file_type === 'contact'"
              :name="data.content"
              :phone-number="attachment.fallback_title"
            />
            <!-- 文件消息 -->
            <BubbleFile v-else :url="attachment.data_url" />
          </div>
        </div>
        <!-- 消息元数据 -->
        <BubbleActions
          :id="data.id"
          :sender="data.sender"
          :story-sender="storySender"
          :external-error="errorMessageTooltip"
          :story-id="`${storyId}`"
          :is-a-tweet="isATweet"
          :is-a-whatsapp-channel="isAWhatsAppChannel"
          :is-email="isEmailContentType"
          :is-private="data.private"
          :message-type="data.message_type"
          :message-status="status"
          :source-id="data.source_id"
          :inbox-id="data.inbox_id"
          :created-at="createdAt"
        />
      </div>
      <Spinner v-if="isPending" size="tiny" />
      <div
        v-if="showAvatar"
        v-tooltip.left="tooltipForSender"
        class="sender--info"
      >
        <woot-thumbnail
          :src="sender.thumbnail"
          :username="senderNameForAvatar"
          size="16px"
        />
        <a
          v-if="isATweet && isIncoming"
          class="sender--available-name"
          :href="twitterProfileLink"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          {{ sender.name }}
        </a>
      </div>
    </div>
    <!-- 消息右侧选项菜单 -->
    <div v-if="shouldShowContextMenu" class="context-menu-wrap">
      <ContextMenu
        v-if="isBubble && !isMessageDeleted"
        :context-menu-position="contextMenuPosition"
        :is-open="showContextMenu"
        :enabled-options="contextMenuEnabledOptions"
        :message="data"
        @open="openContextMenu"
        @close="closeContextMenu"
        @reply-to="handleReplyTo"
      />
    </div>
  </li>
</template>

<style lang="scss">
.wrap {
  > .bubble {
    @apply min-w-[128px]; // minimum width for the bubble

    &.is-unsupported {
      // 使用虚线边框、浅色背景、最大宽度 300px，小字体，包含深色模式适配
      @apply text-xs max-w-[300px] border-dashed border border-slate-200 text-slate-600 dark:text-slate-200 bg-slate-50 dark:bg-slate-700 dark:border-slate-500;

      .message-text--metadata .time {
        @apply text-slate-400 dark:text-slate-300;
      }
    }

    &.is-image,
    &.is-video {
      @apply p-0 overflow-hidden; // 移除内边距并隐藏溢出内容

      .image,
      .video {
        @apply max-w-[20rem] p-0.5; // 设置图片和视频的最大宽度为 20rem

        > img,
        > video {
          /** ensure that the bubble radius and image radius match*/
          @apply rounded-[0.4rem];
        }

        > video {
          @apply h-full w-full object-cover;
        }
      }

      .video {
        @apply h-[11.25rem]; // 视频高度固定为 11.25rem
      }
    }

    &.is-image.is-text > .message-text__wrap,
    &.is-video.is-text > .message-text__wrap {
      @apply max-w-[20rem] py-2 px-4; // 设置最大宽度和内边距
    } // 当图片或视频消息同时包含文本时的样式

    &.is-private .file.message-text__wrap {
      // 私密消息中文件附件的特殊样式
      .file--icon {
        @apply text-woot-400 dark:text-woot-400; // 图标颜色
      }

      .attachment-name {
        @apply text-slate-700 dark:text-slate-200; // 附件名称颜色
      }

      .download.button {
        @apply text-woot-400 dark:text-woot-400; // 下载按钮颜色
      }
    }

    &.is-private.is-text > .message-text__wrap .link {
      @apply text-woot-600 dark:text-woot-200;
    } // 私密消息中链接的颜色样式
    // 私密消息中@提及用户的样式
    &.is-private.is-text > .message-text__wrap .prosemirror-mention-node {
      @apply font-bold bg-none rounded-sm p-0 bg-yellow-100 dark:bg-yellow-700 text-slate-700 dark:text-slate-25 underline; // 使用黄色背景和下划线突出显示
    }

    &.is-from-bot {
      @apply bg-violet-400 dark:bg-violet-400; // 机器人发送的消息使用紫色背景
      // 调整时间戳颜色为浅色
      .message-text--metadata .time {
        @apply text-violet-50 dark:text-violet-50;
      }

      &.is-private .message-text--metadata .time {
        @apply text-slate-400 dark:text-slate-400;
      }
    }

    &.is-failed {
      @apply bg-red-200 dark:bg-red-200; // 发送失败的消息使用红色背景
      // 调整时间戳颜色
      .message-text--metadata .time {
        @apply text-red-50 dark:text-red-50;
      }
    }
  }

  &.is-pending {
    // 消息发送中状态显示为半透明
    @apply relative opacity-80;

    .spinner {
      @apply absolute bottom-1 right-1; // 添加加载旋转图标
    }

    > .is-image.is-text.bubble > .message-text__wrap {
      @apply p-0; // 调整带图片的文本消息内边距
    }
  }
}

.wrap.is-email {
  --bubble-max-width: 84% !important; // 邮件类型消息的最大宽度设置为 84%
}
// 发送者信息的样式，包括头像和名称
.sender--info {
  @apply items-center text-black-700 dark:text-black-100 inline-flex py-1 px-0;

  .sender--available-name {
    @apply text-xs ml-1;
  }
}
// 消息发送失败时的警告提示样式
.message-failed--alert {
  @apply text-red-900 dark:text-red-900 flex-grow text-right mt-1 mr-1 mb-0 ml-0;
}

li.left,
li.right {
  @apply flex items-end; // 使用 flex 布局并底部对齐
} // 左对齐和右对齐消息的基础样式

li.left.has-tweet-menu .context-menu {
  // this handles the offset required to align the context menu button
  // extra alignment is required since a tweet message has a the user name and avatar below it
  @apply mb-6; // 推文消息的上下文菜单需要额外的底部边距
}

li.has-bg {
  @apply bg-woot-75 dark:bg-woot-600; // 消息高亮显示时的背景颜色
}

li.right .context-menu-wrap {
  @apply ml-auto;
}

li.right {
  @apply flex-row-reverse justify-end; // 右侧消息（发出的消息）使用反向 flex 布局
  // 调整发送中和发送失败状态的对齐方式
  .wrap.is-pending {
    @apply ml-auto;
  }

  .wrap.is-failed {
    @apply flex items-end ml-auto;
  }
}

.has-context-menu {
  @apply bg-slate-50 dark:bg-slate-700; // 显示上下文菜单时的背景颜色
}

.context-menu {
  @apply relative; // 菜单本身使用相对定位
}

/* Markdown styling */

.bubble .text-content {
  p code {
    @apply bg-slate-75 dark:bg-slate-700 inline-block leading-none rounded-sm p-1;
  } // 内联代码块的样式，包括背景色和圆角

  ol li {
    @apply list-item list-decimal;
  } // 有序列表项的样式，使用数字标记

  pre {
    @apply bg-slate-75 dark:bg-slate-700 block border-slate-75 dark:border-slate-700 text-slate-800 dark:text-slate-100 rounded-md p-2 mt-1 mb-2 leading-relaxed whitespace-pre-wrap;

    code {
      @apply bg-transparent text-slate-800 dark:text-slate-100 p-0;
    }
  } // 代码块的样式，包括背景色、边框、文本颜色和圆角；保留空白并自动换行

  blockquote {
    @apply border-l-4 mx-0 my-1 pt-2 pr-2 pb-0 pl-4 border-slate-75 border-solid dark:border-slate-600 text-slate-800 dark:text-slate-100;

    p {
      @apply text-slate-800 dark:text-slate-300;
    }
  } // 引用块的样式，左侧有粗边框；调整内边距和文本颜色
}

.right .bubble .text-content {
  p code {
    @apply bg-woot-600 dark:bg-woot-600 text-white dark:text-white;
  }

  pre {
    @apply bg-woot-800 dark:bg-woot-800 border-woot-700 dark:border-woot-700 text-white dark:text-white;

    code {
      @apply bg-transparent text-white dark:text-white;
    }
  }

  blockquote {
    @apply border-l-4 border-solid border-woot-400 dark:border-woot-400 text-white dark:text-white;

    p {
      @apply text-woot-75 dark:text-woot-75;
    }
  }
} // 右侧消息（发出的消息）中 Markdown 元素的特殊样式；调整代码块、内联代码和引用块的颜色，使用蓝色系

/* 翻译内容样式 */
.translation-content {
  @apply mt-2 pt-2 border-t border-slate-100 dark:border-slate-500;
} // 消息翻译内容的样式；顶部有边框分隔原始内容和翻译内容

// 收起翻译样式
.close-translation-row {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.25rem;
}
.close-translation-btn {
  font-size: 12px;
  cursor: pointer;
  user-select: none;
  padding: 0 6px;
  opacity: 0.6;
  transition: color 0.2s;
  &:hover {
    opacity: 1;
  }
}
.arrow-up {
  font-size: 12px;
}
</style>
